import { Action } from 'redux-actions';
import {
	ADD_SERVICE,
	REMOVE_SERVICE,
	ADD_SERVICE_NS,
	REMOVE_SERVICE_NS,
	DELETE_ALL_TYPES_ON_SEGMENT,
	DELETE_ALL_TYPES_ON_SEGMENT_NS,
	DeleteAllOnSegmentAction,
	ServicePayload,
	SET_ALL_SELECTED_SERVICES,
	SET_MULTIPLE_SELECTED_SERVICES,
	SET_MULTIPLE_SELECTED_SERVICES_NS,
	SET_SERVICE,
	SetServicesAction,
	ServicePayloadExtraValues
} from './actions';
import { Money } from '../../../types';
import { getRelatedSegments } from '../../../Modules/Checkin/utils';
import { setsAreEqual } from '../../../utils';
import {
	SeatType,
	OrderAdditionalServiceGdsServiceServiceType,
	RowSeatFragment,
	PassengerSelectedGdsServicesProductFragment,
	SeatMapRestrictions
} from '@websky/graphql';
import { useSelector } from 'react-redux';
export interface SelectedServiceType {
	id: string;
	type: OrderAdditionalServiceGdsServiceServiceType;
	confirmedCount?: number;
	price?: Money;
	name?: string;
}

export interface SelectedSeatsServiceType {
	id: string;
	type: OrderAdditionalServiceGdsServiceServiceType;
	deckId?: string;
	letter?: string;
	row?: string;
	number?: string;
	rfisc?: string;
	price?: Money;
	segmentId: string;
	isConfirmed?: boolean;
	isChangeable?: boolean;
	onRequest?: boolean;
	seatType?: SeatType;
	seatServices?: RowSeatFragment['seatServices'];
	seatService?: RowSeatFragment['seatService'];
	product?: PassengerSelectedGdsServicesProductFragment;
	restrictions?: SeatMapRestrictions[];
}

export interface SelectedService {
	segments: string[];
	servicePayload: SelectedServiceType | SelectedSeatsServiceType;
	number: number;
	extraValues?: ServicePayloadExtraValues[];
}

export interface PassengerServices {
	[serviceId: string]: SelectedService[];
}

export interface SelectedServicesState {
	[passengerId: string]: PassengerServices;
}

export const getNewCount = (currentCount: number, countToDelete: number, confirmedCount: number): number => {
	const result = currentCount - countToDelete;

	return confirmedCount > result ? confirmedCount : result;
};

const getConfirmedCount = (servicePayload: SelectedServiceType | SelectedSeatsServiceType): number => {
	if (servicePayload.type === OrderAdditionalServiceGdsServiceServiceType.Seat) {
		return (servicePayload as SelectedSeatsServiceType).isConfirmed ? 1 : 0;
	} else {
		return (servicePayload as SelectedServiceType).confirmedCount
			? (servicePayload as SelectedServiceType).confirmedCount
			: 0;
	}
};

const pushServiceToState = (passengerState: PassengerServices, service: ServicePayload): PassengerServices => {
	const segmentIds = service.allowedSegments
		? getRelatedSegments(service.allowedSegments, service.segmentId)
		: new Set([service.segmentId]);

	return {
		...passengerState,
		[service.serviceId]: [
			...(passengerState && passengerState[service.serviceId] ? passengerState[service.serviceId] : []),
			{
				segments: [...segmentIds],
				servicePayload: service.service,
				number: service.count >= 0 ? service.count : 1,
				extraValues: service.extraValues
			}
		]
	};
};

const setServicesByPassengers = (state: SelectedServicesState, services: ServicePayload[]): SelectedServicesState => {
	let newState = {
		...state
	};

	services.forEach(service => {
		newState = {
			...newState,
			[service.passengerId]: mergeServicesWithSameId(newState[service.passengerId] || {}, service, true)
		};
	});

	return newState;
};

export function useGetState() {
	return useSelector(state => state);
}

export const selectedServicesReducer = (
	state: SelectedServicesState = {},
	action: Action<ServicePayload | ServicePayload[] | SetServicesAction | DeleteAllOnSegmentAction>
): SelectedServicesState => {
	if (action.type === ADD_SERVICE || action.type === ADD_SERVICE_NS) {
		const payload = action.payload as ServicePayload;

		if (isServiceIdExists(state, payload)) {
			return {
				...state,
				[payload.passengerId]: {
					...mergeServicesWithSameId(state[payload.passengerId], payload)
				}
			};
		} else {
			return {
				...state,
				[payload.passengerId]: {
					...pushServiceToState(state[payload.passengerId], payload)
				}
			};
		}
	} else if (action.type === REMOVE_SERVICE || action.type === REMOVE_SERVICE_NS) {
		const payload = action.payload as ServicePayload;

		return {
			...state,
			[payload.passengerId]: {
				...removeService(state[payload.passengerId], payload)
			}
		};
	} else if (action.type === SET_SERVICE) {
		const payload = action.payload as ServicePayload;

		return {
			...state,
			[payload.passengerId]: {
				...mergeServicesWithSameId(state[payload.passengerId], payload, true)
			}
		};
	} else if (action.type === SET_ALL_SELECTED_SERVICES) {
		const payload = action.payload as SetServicesAction;

		return payload;
	} else if (action.type === SET_MULTIPLE_SELECTED_SERVICES || action.type === SET_MULTIPLE_SELECTED_SERVICES_NS) {
		const payload = action.payload as ServicePayload[];

		return setServicesByPassengers(state, payload);
	} else if (action.type === DELETE_ALL_TYPES_ON_SEGMENT || action.type === DELETE_ALL_TYPES_ON_SEGMENT_NS) {
		// Удаляем все услуги с заданным типом у всех пассажиров на заданном сегменте или на всех сегментах
		const payload = action.payload as DeleteAllOnSegmentAction;
		let newState: SelectedServicesState = state;
		for (const passengerId in state) {
			if (state.hasOwnProperty(passengerId)) {
				for (const passengerServiceId in state[passengerId]) {
					if (state[passengerId].hasOwnProperty(passengerServiceId)) {
						newState = {
							...newState,
							[passengerId]: {
								...newState[passengerId],
								[passengerServiceId]: newState[passengerId][passengerServiceId].map(service => {
									if (service.servicePayload.id === payload.serviceId) {
										if (payload.deleteOnAllSegments) {
											return {
												...service,
												number: 0
											};
										}

										if (service.segments.includes(payload.segmentId)) {
											return {
												...service,
												number: 0
											};
										}

										return { ...service };
									}

									return service;
								})
							}
						};
					}
				}
			}
		}

		return newState;
	}

	return state;
};

const isServiceIdExists = (state: SelectedServicesState, service: ServicePayload): boolean => {
	return !!state[service.passengerId]?.[service.serviceId];
};

const mergeServicesWithSameId = (
	passengerServices: PassengerServices,
	newService: ServicePayload,
	setCountOfServices?: boolean
): PassengerServices => {
	const existServices = passengerServices?.[newService.serviceId];

	const relatedSegments = newService.allowedSegments
		? getRelatedSegments(newService.allowedSegments, newService.segmentId)
		: new Set([newService.segmentId]);

	// Находим услугу, у которой такой же набор сегментов.
	const sameServiceIndex = existServices?.findIndex(service => {
		return setsAreEqual(new Set(service.segments), relatedSegments);
	});

	// если такая услуга есть, то увеличиваем кол-во на 1
	if (sameServiceIndex >= 0) {
		return {
			...passengerServices,
			[newService.serviceId]: passengerServices[newService.serviceId].map((service, index) => {
				if (index === sameServiceIndex) {
					return {
						...service,
						number: setCountOfServices
							? newService.count ?? service.number + (newService.count >= 0 ? newService.count : 1)
							: service.number + (newService.count >= 0 ? newService.count : 1)
					};
				}

				return service;
			})
		};
	} else {
		// если нет, то добавляем как новую
		return {
			...pushServiceToState(passengerServices, newService)
		};
	}
};

const removeService = (passengerState: PassengerServices, service: ServicePayload): PassengerServices => {
	const existServicesIndex = passengerState[service.serviceId]?.findIndex(existService => {
		return existService.segments.includes(service.segmentId);
	});

	if (existServicesIndex >= 0) {
		const existService = passengerState[service.serviceId][existServicesIndex];

		if (existService.number > 0) {
			return {
				...passengerState,
				[service.serviceId]: passengerState[service.serviceId].map((state, index) => {
					if (index === existServicesIndex) {
						return {
							...state,
							number: getNewCount(
								state.number,
								service.count ? service.count : 1,
								getConfirmedCount(state.servicePayload)
							)
						};
					}

					return state;
				})
			};
		}
	}

	return passengerState;
};
